/**
 * menteeDisplayName — SSOT for "what string do we render for this
 * mentee?". The fallback chain is intentionally non-trivial
 * (full-name → email → "Mentee #id") and was previously duplicated
 * in 4+ files, drifting subtly. One file edit now updates every
 * surface that names a mentee — the V3 SSOT discipline.
 *
 * Pure. No React. No DOM. Easy to unit-test in isolation.
 *
 * see docs/mentorship-flow.md for the V3 SSOT discipline rationale.
 */

/** Structural shape covering both the wire `UserMini` and the
 *  dashboard's `UserLike`. Decouples this helper from a single
 *  service module so it can be reused by any surface that has a
 *  user-shaped object. */
export type MenteeLike = {
    id: number;
    first_name?: string;
    last_name?: string;
    email?: string;
};

/** Render a human-facing label for a mentee.
 *  Fallback chain:
 *    1. Full name (first + last, whitespace-trimmed)
 *    2. Email (whitespace-trimmed — returning a padded email caused
 *       a duplicate-info bug in the focus card sublabel where the
 *       label and sublabel rendered the same address with different
 *       whitespace and failed the redundancy dedup)
 *    3. "Mentee #<id>" — never a blank pill */
export function menteeDisplayName(m: MenteeLike): string {
    const first = m.first_name ?? '';
    const last = m.last_name ?? '';
    const name = `${first} ${last}`.trim();
    if (name) return name;
    const email = m.email?.trim();
    if (email) return email;
    return `Mentee #${m.id}`;
}
